import { Link } from 'react-router-dom';
import type { LiveSession } from '@/mocks/lives';
import { formatCount, statusText } from '@/utils/time';
import ReminderButton from '@/components/feature/ReminderButton';

const dotStyle: Record<string, string> = {
  live: 'border-accent-500 bg-accent-100',
  upcoming: 'border-primary-400 bg-primary-50',
  ended: 'border-background-400 bg-background-100',
};

export default function ScheduleRow({ session }: { session: LiveSession }) {
  return (
    <li className="flex gap-3 md:gap-6">
      <div className="w-14 shrink-0 pt-4 text-right md:w-20">
        <div className="font-heading text-base font-bold tabular-nums text-foreground-950 md:text-lg">
          {session.start}
        </div>
        <div className="mt-0.5 text-[11px] text-foreground-400">至 {session.end}</div>
      </div>

      <div className="relative flex flex-col items-center pt-5">
        <span className={`h-3 w-3 shrink-0 rounded-full border-2 ${dotStyle[session.status]}`} />
        <span className="mt-1 w-px flex-1 bg-background-200" />
      </div>

      <div className="flex-1 pb-6">
        <article className="overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
          <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start md:p-5">
            <Link
              to={`/live/${session.id}`}
              className="relative block w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:w-[168px]"
              aria-label={session.title}
            >
              <span className="block aspect-[16/9] w-full">
                <img
                  src={session.cover}
                  alt={`爆料直播网 - ${session.title} 直播封面`}
                  title={`${session.title} 爆料直播 今日直播排期`}
                  className="h-full w-full object-cover object-top"
                />
              </span>
            </Link>

            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded bg-secondary-100 px-2 py-0.5 font-medium text-secondary-900">
                  {session.category}
                </span>
                <span
                  className={`flex items-center gap-1 font-medium ${
                    session.status === 'live'
                      ? 'text-accent-600'
                      : session.status === 'upcoming'
                        ? 'text-primary-700'
                        : 'text-foreground-400'
                  }`}
                >
                  {session.status === 'live' && (
                    <span className="h-1.5 w-1.5 rounded-full bg-accent-500 animate-pulse" />
                  )}
                  {statusText[session.status]}
                </span>
                {session.status !== 'upcoming' && session.viewers > 0 && (
                  <span className="text-foreground-500">· {formatCount(session.viewers)} 人在看</span>
                )}
              </div>

              <h3 className="mt-2">
                <Link
                  to={`/live/${session.id}`}
                  className="font-heading text-[15px] font-bold leading-snug text-foreground-950 transition-colors hover:text-primary-700 md:text-base"
                >
                  {session.title}
                </Link>
              </h3>

              <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-[13px]">
                {session.summary}
              </p>

              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-[10px] font-semibold text-primary-800">
                    {session.host.slice(0, 1)}
                  </span>
                  {session.host}
                </span>
                {session.tags.map((tag) => (
                  <span key={tag} className="rounded border border-background-300 px-2 py-0.5 text-foreground-600">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-3.5 flex flex-wrap items-center gap-3">
                <Link
                  to={`/live/${session.id}`}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
                >
                  查看场次详情
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </span>
                </Link>
                {session.status !== 'ended' && <ReminderButton sessionId={session.id} />}
              </div>
            </div>
          </div>
        </article>
      </div>
    </li>
  );
}